import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdArrowDropdown } from 'react-icons/io';
import Ca from '../../assets/ca.svg';
import Us from '../../assets/us.svg';
import Mx from '../../assets/mx.svg';
import SpinnerIcon from '../../assets/spinner4.svg';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { useNavigate } from 'react-router-dom';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';
import api from '../../api/api';
import useToast from '../../hooks/useToast';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import Toast from '../Toast';

const EditProfile = ({ isEditProfileModalOpen, onClose }) => {
  const { t } = useTranslation();
  const { toastMessage, toastType, showToast } = useToast();
  const { user, updateProfile, logout } = useUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const countries = [
    { flag: Ca, code: '+1', name: 'Canada' },
    { flag: Us, code: '+1', name: 'United States' },
    { flag: Mx, code: '+52', name: 'Mexico' },
  ];
  const defaultCountry = countries.find(
    c => c.code === user?.phoneNumber?.countryCode,
  ) || { flag: Ca, code: '+1', name: 'Canada' };
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  const dropdownRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  useBodyScrollLock(isEditProfileModalOpen || isAnimating);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber?.number || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { clearCart } = useCart();
  const navigate = useNavigate();

  // Open Country Codes Dropdown & select country 
  const handleOutsideClick = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleDropdownToggle = () => {
    setDropdownOpen(prev => !prev);
  };

  const handlePhoneNumberChange = (e) => {
    const onlyNums = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setPhoneNumber(onlyNums);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  useEffect(() => {
    if (isEditProfileModalOpen) {
      setIsAnimating(true);
    } else if (isAnimating) {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isEditProfileModalOpen, isAnimating]);

  const validateFields = () => {
    const newErrors = {};
    const nameRegex = /^[\p{L}][\p{L}\p{M}\p{N}'\- ]*$/u; // letters only at start, then letters, accents, numbers, spaces, hyphens, apostrophes

    const phoneRegex = /^\d{6,12}$/; // 6–12 digits only
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!firstName.trim()) {
      newErrors.firstName = t('firstnameerror1');
    } else if (!nameRegex.test(firstName)) {
      newErrors.firstName = t('firstnameerror4');
    } else if (firstName.length > 70) {
      newErrors.firstName = t('firstnameerror5');
    }

    if (!lastName.trim()) {
      newErrors.lastName = t('lastnameerror1');
    } else if (!nameRegex.test(lastName)) {
      newErrors.lastName = t('lastnameerror4');
    } else if (lastName.length > 70) {
      newErrors.lastName = t('lastnameerror5');
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = t('phonenumbererror2');
    } else if (!phoneRegex.test(phoneNumber)) {
      newErrors.phoneNumber = t('phonenumbererror1');
    }

    if (!email.trim()) {
      newErrors.email = t('emailerror1');
    } else if (!emailRegex.test(email)) {
      newErrors.email = t('emailerror2');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) {
      return;
    }
    setIsLoading(true);

    const token = localStorage.getItem('idToken');

    try {
      const response = await api.put(
        '/user/profile',
        {
          firstName,
          lastName,
          phoneNumber: {
            countryCode: selectedCountry.code,
            number: phoneNumber,
          },
          newEmail: email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      log.log('✅ Success:', response.data);

      const { data, forceLogout } = response.data.response.data;

      if (forceLogout) {
        logout();
        clearCart();
        navigate('/home');
      }
      else {
        updateProfile({
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
          email: data.email,
        });
      }
      onClose();
    } catch (error) {
      log.error('Failed to update profile:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div
        className={`fixed bottom-0 lg:top-0 left-0 w-full max-w-full lg:max-w-[550px] max-h-[85dvh] lg:h-full bg-white z-50 p-6 overflow-y-auto rounded-t-2xl lg:rounded-none transition-transform duration-300 ${isAnimating ? 'modalanimated' : 'whennotmodalclose'}`}
      >

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <h1 className='text-2xl font-normal mb-3'>
            {t('Edit Profile')}
          </h1>

          {/* First Name */}
          <div className='mt-5'>
            <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>
              {t('First name')} <span className='text-[#ef4444]'>*</span>
            </label>
            <input
              type="text"
              className={`rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0
                ${firstName.length === 0 ? 'border-red-500' : 'border-[#E5E7EB]'}`}
              value={firstName}
              maxLength={70}
              onChange={(e) => setFirstName(e.target.value.slice(0, 70))}
            />
            {firstName.length === 0 && !errors.firstName && (
              <span className="text-xs text-red-500">{t('fieldMandatory')}</span>
            )}
            {errors.firstName && <span className="text-xs text-red-500">{errors.firstName}</span>}
            <div className="text-xs text-gray-600 text-right">{firstName.length}/70</div>
          </div>

          {/* Last Name */}
          <div className='mt-5'>
            <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>
              {t('Last name')} <span className='text-[#ef4444]'>*</span>
            </label>
            <input
              type="text"
              className={`rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0
                ${lastName.length === 0 ? 'border-red-500' : 'border-[#E5E7EB]'}`}
              value={lastName}
              maxLength={70}
              onChange={(e) => setLastName(e.target.value.slice(0, 70))}
            />
            {lastName.length === 0 && !errors.lastName && (
              <span className="text-xs text-red-500">{t('fieldMandatory')}</span>
            )}
            {errors.lastName && <span className="text-xs text-red-500">{errors.lastName}</span>}
            <div className="text-xs text-gray-600 text-right">{lastName.length}/70</div>
          </div>

          {/* Phone Number  */}
          <div className='mt-5'>
            <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>
              {t('phone')} <span className='text-[#ef4444]'>*</span>
            </label>
            <div className={`flex items-center relative justify-between gap-2 rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0
              ${phoneNumber.length === 0 ? 'border-red-500' : 'border-[#E5E7EB]'}`}
            >
              <div
                className='flex items-center gap-1 cursor-pointer'
                onClick={handleDropdownToggle}
              >
                <img src={selectedCountry.flag} alt="flag" className='w-[23px] h-[23px]' />
                <IoMdArrowDropdown />
              </div>

              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                className='placeholder:font-light w-full outline-none h-full flex-1 bg-transparent border-none'
              />

              {/* Countries Dropdown  */}
              {dropdownOpen && (
                <div
                  ref={dropdownRef}
                  className='bg-white border border-[#ccc] p-[9px] w-full max-w-[390px] rounded-[.3rem] absolute top-[55px] left-0 z-50'
                >
                  <input
                    type="text"
                    placeholder='Search'
                    className='placeholder:font-light w-full outline-none h-[2.5rem] flex-1 bg-transparent border-2 border-black border-opacity-30 rounded-[.4rem] px-[.4rem]'
                  />

                  <ul className='min-h-[8.5rem] h-[8.5rem] overflow-y-scroll boxscrollbar my-2'>
                    {countries.map(country => (
                      <li key={country.name} className='py-1 px-[.4rem] flex items-center cursor-pointer text-base' onClick={() => { setSelectedCountry(country); setDropdownOpen(false); }}>
                        <img src={country.flag} alt="flag" className='w-[23px] h-[23px] mr-2' />
                        <p className='mr-1 font-semibold'>{country.name}</p>
                        <p>{country.code}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {phoneNumber.length === 0 && !errors.phoneNumber && (
              <span className="text-xs text-red-500">{t('fieldMandatory')}</span>
            )}
            {errors.phoneNumber && <span className="text-xs text-red-500">{errors.phoneNumber}</span>}
          </div>

          <div className='mt-5'>
            <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>{t('email')} <span className='text-[#ef4444]'>*</span></label>
            <input
              type="email"
              className={`rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0
                ${email.length === 0 ? 'border-red-500' : 'border-[#E5E7EB]'}`}
              value={email}
              onChange={handleEmailChange}
            />
            {email.length === 0 && !errors.email && (
              <span className="text-xs text-red-500">{t('fieldMandatory')}</span>
            )}
            {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
          </div>

          <div className='flex items-center justify-between gap-2 my-10'>
            <button className='hidden text-[#E53935] font-medium text-sm uppercase h-9 flex items-center justify-center hover:bg-[#E53935] px-4 hover:bg-opacity-10 rounded-md transition-all duration-500'>{t('Delete Account')}</button>

            <div className='flex gap-2'>
              <button onClick={onClose} className='text-[#1E88E5] font-medium h-9 px-4 flex items-center justify-center text-sm uppercase hover:bg-[#1E88E5] hover:bg-opacity-10 rounded-md transition-all duration-500'>{t('Cancel')}</button>
              <button 
                type="submit"
                className='text-[#1E88E5] font-medium h-9 px-4 flex items-center justify-center text-sm uppercase hover:bg-[#1E88E5] hover:bg-opacity-10 rounded-md transition-all duration-500'
                disabled={isLoading}
              >
                {isLoading ? (
                  <img
                    src={SpinnerIcon}
                    alt="Loading..."
                    className="h-6 w-6 animate-spin mx-auto"
                  />
                ) : (
                  t('Save')
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <Toast message={toastMessage} type={toastType} />

      {/* Overlay */}
      {isEditProfileModalOpen && (
        <div
          className='fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40'
          onClick={onClose}
        />
      )}
    </div>
  );
};

export default EditProfile;
